"use client";

import { useState, useEffect, useRef } from "react";
import { Play, Pause } from "lucide-react";
import { Link } from "react-router-dom";
import { sections } from "./data.js";
import { useTranslation } from "react-i18next";

export default function VideoShowcase() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const intervalRef = useRef(null);
  const videoRef = useRef(null);

  // Map sections with translated strings
  const translatedSections = sections.map(section => ({
    ...section,
    title: t(section.titleKey),
    description: t(section.descriptionKey),
    buttonText: t(section.buttonTextKey)
  }));

  // Calculate total duration of all sections
  const totalDuration = translatedSections.reduce(
    (acc, section) => acc + section.duration,
    0
  );

  // Calculate the percentage each section takes of the total progress
  const sectionPercentages = translatedSections.map(
    (section) => (section.duration / totalDuration) * 100
  );

  // Calculate the start percentage for each section
  const sectionStartPercentages = sectionPercentages.reduce(
    (acc, percentage, index) => {
      if (index === 0) return [0];
      return [...acc, acc[index - 1] + sectionPercentages[index - 1]];
    },
    [0]
  );

  // Safe video play function with error handling
  const safePlayVideo = async () => {
    if (videoRef.current) {
      try {
        await videoRef.current.play();
        setVideoError(false);
      } catch (error) {
        // Handle specific error types
        if (error.name === "NotAllowedError") {
          // Autoplay was prevented by browser policy
          console.log("Autoplay prevented by browser policy");
          setVideoError(true);
        } else if (error.name === "AbortError") {
          // Video was interrupted (e.g., for power saving)
          console.log("Video playback was interrupted");
          // Don't set videoError for this case, just log it
        } else {
          console.log("Video playback error:", error);
          setVideoError(true);
        }
      }
    }
  };

  // Safe video pause function
  const safePauseVideo = () => {
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (error) {
        console.log("Video pause error:", error);
      }
    }
  };

  useEffect(() => {
    if (isPlaying && !videoError) {
      safePlayVideo();
    } else {
      safePauseVideo();
    }
  }, [isPlaying, activeSection, videoError]);

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          // Calculate increment based on current section's duration
          const increment = 100 / (translatedSections[activeSection].duration * 10);
          const newProgress = prev + increment;

          if (newProgress >= 100) {
            // Move to next section
            const nextSection = (activeSection + 1) % translatedSections.length;
            setActiveSection(nextSection);
            return 0;
          }

          return newProgress;
        });
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, activeSection, translatedSections]);

  // Reset video error when section changes
  useEffect(() => {
    setVideoError(false);
  }, [activeSection]);

  const handleSectionClick = (index) => {
    setActiveSection(index);
    setProgress(0);
    setVideoError(false);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSectionClick(index);
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <section className="w-full py-16 md:py-24 lg:py-32 bg-gradient-to-br ">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-red-500">
              {t("gift.neverMissGift")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-neutral-green-500">
              {t("career.heroDesc")}
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Sections */}
            <div className="relative">
              {/* Continuous Background Line */}
              <div className="absolute left-0.25 top-0 bottom-0 w-0.5 bg-slate-200 rounded-full"></div>

              {/* Active Section Thick Line Segment */}
              <div
                className="absolute left-0 w-1 bg-slate-200 rounded-full transition-all duration-300"
                style={{
                  top: `${(activeSection / translatedSections.length) * 100}%`,
                  height: `${100 / translatedSections.length}%`,
                }}
              >
                {/* red Progress Fill */}
                <div
                  className="w-full bg-red-500 transition-all duration-100 ease-linear rounded-full"
                  style={{
                    height: `${progress}%`,
                    transformOrigin: "top",
                  }}
                />
              </div>

              {/* Section Content */}
              <div className="space-y-16">
                {translatedSections.map((section, index) => {
                  // Calculate if this section is active, completed, or upcoming
                  const isActive = index === activeSection;
                  const isCompleted = index < activeSection;

                  return (
                    <div
                      key={section.id}
                      className={`relative cursor-pointer transition-all duration-300 ${isActive ? "opacity-100" : "opacity-60 hover:opacity-80"
                        }`}
                      onClick={() => handleSectionClick(index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      role="button"
                      tabIndex={0}
                    >
                      {/* Content */}
                      <div className="pl-8">
                        <h3
                          className={`text-xl md:text-2xl font-semibold mb-2 transition-colors ${isActive
                            ? "text-red-500"
                            : isCompleted
                              ? "text-slate-500"
                              : "text-slate-600"
                            }`}
                        >
                          {section.title}
                        </h3>

                        {isActive && (
                          <div className="space-y-3 animate-in slide-in-from-left-2 duration-300">
                            <h4 className="text-lg font-medium text-slate-700">
                              {section.subtitle}
                            </h4>
                            <p className="text-slate-600 leading-relaxed">
                              {section.description}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Side - Video */}
            <div className="relative">
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="aspect-video bg-gradient-to-br from-red-100 to-purple-100 flex items-center justify-center">
                  {translatedSections[activeSection].type === "video" ? (
                    <video
                      ref={videoRef}
                      key={translatedSections[activeSection].videoUrl}
                      src={translatedSections[activeSection].videoUrl}
                      className="w-full h-full object-cover"
                      playsInline
                      muted
                      loop
                      onError={(e) => {
                        console.log("Video error:", e);
                        setVideoError(true);
                      }}
                      onAbort={(e) => {
                        console.log("Video aborted:", e);
                        // Don't set videoError for abort events (power saving, etc.)
                      }}
                    />
                  ) : (
                    <img
                      src={
                        translatedSections[activeSection].videoUrl || "/placeholder.svg"
                      }
                      alt={translatedSections[activeSection].title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Simplified Video Controls */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center justify-between">
                    {/* Play/Pause Button */}
                    <button
                      onClick={togglePlayPause}
                      className="flex items-center justify-center w-10 h-10 bg-black/20 hover:bg-black/30 backdrop-blur-sm rounded-full transition-colors"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5 text-white" />
                      ) : (
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      )}
                    </button>

                    {/* Create a Reminder Button */}
                    <Link
                      to={translatedSections[activeSection].buttonLink}
                      className="bg-black/20 hover:bg-black/30 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
                    >
                      {translatedSections[activeSection].buttonText}
                    </Link>
                  </div>
                </div>
              </div>

              {/* Section Indicators */}
              <div className="flex justify-center mt-6 gap-2">
                {translatedSections.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleSectionClick(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${index === activeSection
                      ? "bg-red-500 w-8"
                      : "bg-slate-300 hover:bg-slate-400"
                      }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
